import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCheckCircle,
  MdOutlineError,
  MdOutlineExplore,
  MdOutlineRefresh,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { resolveError } from "./constants";
import StepCard from "./StepCard";

const StepResult = ({ result, onFinish, onReset }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const IMPORT = t("IMPORT", { returnObjects: true });

  const success = get(result, "success", false);
  const errors = get(result, "errors", []);
  const pricersCreated = get(result, "pricersCreated", 0);
  const pricersUpdated = get(result, "pricersUpdated", 0);
  const pricersSkipped = get(result, "pricersSkipped", 0);
  const totalRows = get(result, "totalRows", 0);
  const startDate = get(result, "startDate", "—");
  const endDate = get(result, "endDate", "—");
  const strategy = get(result, "strategy", "");

  const resultActions = (
    <>
      <Button type="primary--outlined" onClick={onReset}>
        <MdOutlineRefresh size={20} />
        {IMPORT.RESULT.IMPORT_ANOTHER}
      </Button>
      <Button type="primary" onClick={onFinish}>
        <MdOutlineExplore size={20} />
        {IMPORT.RESULT.VIEW_PRICERS}
      </Button>
    </>
  );

  return (
    <StepCard actions={resultActions}>
      <div className="pricers-import__result-container">
        <div
          className={classNames("pricers-import__result-icon", {
            "pricers-import__result-icon--success": success,
            "pricers-import__result-icon--failure": !success,
          })}
        >
          {success ? (
            <MdOutlineCheckCircle size={48} color={COLORS.C_SUCCESS} />
          ) : (
            <MdOutlineError size={48} color={COLORS.C_DANGER} />
          )}
        </div>
        <h2>
          {success ? IMPORT.RESULT.SUCCESS_TITLE : IMPORT.RESULT.FAILURE_TITLE}
        </h2>
        {success && strategy && (
          <p className="pricers-import__result-strategy">
            {IMPORT.RESULT.STRATEGY}:{" "}
            {get(IMPORT, `ANALYSIS.STRATEGIES.${strategy}.LABEL`, strategy)}
          </p>
        )}
      </div>

      {!success && errors.length > 0 && (
        <div className="pricers-import__error-section">
          <h4 className="pricers-import__error-title">
            {IMPORT.RESULT.ERROR_DETAILS}
          </h4>
          <div className="pricers-import__error-list">
            {errors.map((err, i) => (
              <div key={i} className="pricers-import__error-item">
                {resolveError(err, IMPORT.ERROR_KEYS)}
              </div>
            ))}
          </div>
        </div>
      )}

      {success && (
        <>
          <div className="grid--3 gap-16 mb-16">
            <div className="pricers-import__result-stat pricers-import__result-stat--created">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--created">
                {pricersCreated}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.CREATED}
              </div>
            </div>
            <div className="pricers-import__result-stat pricers-import__result-stat--updated">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--updated">
                {pricersUpdated}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.UPDATED}
              </div>
            </div>
            <div className="pricers-import__result-stat pricers-import__result-stat--skipped">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--skipped">
                {pricersSkipped}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.SKIPPED}
              </div>
            </div>
          </div>

          <div className="pricers-import__result-footer">
            <span>
              {IMPORT.RESULT.PERIOD}: {startDate} → {endDate}
            </span>
            <span>
              {totalRows} {IMPORT.RESULT.ROWS_PROCESSED}
            </span>
          </div>
        </>
      )}
    </StepCard>
  );
};

export default StepResult;
